/** @file Camera = world-to-pixel affine plus target size; fitCamera frames a bounds box (y-up to y-down). */
import type { Affine } from '@pose2d/math/types';

export interface Camera {
  /** Maps skeleton/world coordinates (y-up) to pixel coordinates (y-down, origin top-left). */
  worldToPixel: Affine;
  width: number;
  height: number;
}

/**
 * Builds a camera whose viewport contains `bounds` ([minX, minY, maxX, maxY], world units) centered,
 * with `pad` pixels of margin on every side. Uniform scale; flips Y.
 */
export function fitCamera(bounds: ArrayLike<number>, width: number, height: number, pad: number): Camera {
  const minX = bounds[0] ?? 0, minY = bounds[1] ?? 0, maxX = bounds[2] ?? 0, maxY = bounds[3] ?? 0;
  // Degenerate bounds (a single point) frame one world unit; padding never exceeds the viewport.
  const w = Math.max(maxX - minX, 1);
  const h = Math.max(maxY - minY, 1);
  const zoom = Math.min(Math.max(width - 2 * pad, 1) / w, Math.max(height - 2 * pad, 1) / h);
  const cx = (minX + maxX) / 2;
  const cy = (minY + maxY) / 2;
  const tx = width / 2 - cx * zoom;
  const ty = height / 2 + cy * zoom;
  return { worldToPixel: [zoom, 0, 0, -zoom, tx, ty], width, height };
}
